import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Instagram, Facebook } from "lucide-react";
import { virksomhedsinfo } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { label: "Forside", href: "/" },
    { label: "Behandlinger", href: "/behandlinger" },
    { label: "Om mig", href: "/om-mig" },
    { label: "Kontakt", href: "/kontakt" },
  ];

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname === path;
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#EAE5DF] transition-all duration-300">
      {/* Top bar med hurtig info */}
      <div className="bg-[#4A5D4E] text-[#FAF8F5] py-2 px-4 sm:px-6 text-xs font-medium tracking-wide">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 animate-pulse mr-1" />
            <span>Klinik i Odense N · Professionel fodpleje med fokus på ro og nærvær</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`tel:+45${virksomhedsinfo.telefon.replace(/\s+/g, "")}`} className="hover:text-[#E8EFEA] transition-colors flex items-center gap-1 whitespace-nowrap">
              <Phone className="h-3.5 w-3.5" />
              <span>Tlf: {virksomhedsinfo.telefon}</span>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo og Kliniknavn */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative h-16 w-16 xl:h-[100px] xl:w-[100px] rounded-full overflow-hidden bg-white flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-300">
              <img
                src={virksomhedsinfo.logo}
                alt="RG Fodpleje Logo"
                className="h-full w-full object-cover"
                onError={(e) => {
                  // Fallback hvis billedet fejler
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </Link>

          {/* Desktop Navigation (Skjules under 1200px - xl breakpoint) */}
          <nav className="hidden xl:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`relative py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive(item.href)
                    ? "text-[#4A5D4E]"
                    : "text-[#6B6661] hover:text-[#4A5D4E]"
                }`}
              >
                {item.label}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#4A5D4E] rounded-full" />
                )}
              </Link>
            ))}
            
            {/* Book tid knap */}
            <a
              href="https://rg-fodpleje.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-4 bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-6 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              Book tid online
            </a>
          </nav>

          {/* Sociale medier i header (Desktop) */}
          <div className="hidden xl:flex items-center gap-4 border-l border-[#EAE5DF] pl-6 ml-2">
            <a
              href={virksomhedsinfo.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#6B6661] hover:text-[#4A5D4E] transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href={virksomhedsinfo.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#6B6661] hover:text-[#4A5D4E] transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>

          {/* Mobil menu-knap (Vises under 1200px - xl breakpoint) */}
          <div className="flex items-center xl:hidden gap-3">
            <a
              href="https://rg-fodpleje.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-4 py-2 rounded-full text-xs font-medium tracking-wide transition-all duration-300"
            >
              Book tid
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-[#6B6661] hover:text-[#4A5D4E] hover:bg-[#F3EFEA] transition-colors focus:outline-none"
              aria-label="Åbn menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobil Navigation (Skuffe-menu) */}
      {isOpen && (
        <div className="xl:hidden bg-[#FAF8F5] border-t border-[#EAE5DF] py-4 px-4 space-y-3 animate-fadeIn">
          <div className="flex flex-col space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={`px-4 py-3 rounded-lg text-base font-medium tracking-wide transition-colors ${
                  isActive(item.href)
                    ? "bg-[#E8EFEA] text-[#2E4233]"
                    : "text-[#6B6661] hover:bg-[#F3EFEA] hover:text-[#4A5D4E]"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="pt-4 border-t border-[#EAE5DF] flex flex-col gap-4 px-4">
            <a
              href="https://rg-fodpleje.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] py-3 rounded-full text-center text-sm font-medium tracking-wide transition-colors shadow-sm"
            >
              Book tid online
            </a>
            
            <div className="flex justify-center gap-6 py-2">
              <a
                href={virksomhedsinfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#6B6661] hover:text-[#4A5D4E] transition-colors p-2 bg-[#F3EFEA] rounded-full"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href={virksomhedsinfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#6B6661] hover:text-[#4A5D4E] transition-colors p-2 bg-[#F3EFEA] rounded-full"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
